import * as Sentry from '@sentry/react';

import getIntegrations from '../integrations';
import { Config } from '../types';
import { getEnvironment, getInitialScope, isBrowserSupported, prepareConfig } from './utils';

// eslint-disable-next-line camelcase, no-var
declare var __webpack_public_path__: string;

type PossibleError = Error | PromiseRejectionEvent;

const isCurrentServiceError = ({
    error,
    publicBuildPath,
    url,
}: {
    error: PossibleError;
    publicBuildPath: string;
    url?: string;
}) => {
    const errorSources = [];
    const isError = (error: PossibleError): error is Error => 'stack' in error;
    const isRejectionError = (error: PossibleError): error is PromiseRejectionEvent => 'reason' in error;
    url && errorSources.push(url);
    isError(error) && errorSources.push(error.stack);
    isRejectionError(error) && errorSources.push((error.reason as Record<string, string>)?.stack);

    // eslint-disable-next-line camelcase
    const webpackBuildPath = publicBuildPath || __webpack_public_path__;
    return errorSources.some((errorSource) => errorSource?.includes?.(webpackBuildPath));
};

/**
 * Для того, чтобы добавить какую-то ошибку в ignore,
 * нужно поправить динамическую настройку sentry_js_config в сервисе.
 */
interface InitResult {
    client?: Sentry.BrowserClient;
    scope?: Sentry.Scope;
}

export const initBrowserClient = (config: Config): InitResult => {
    const { sentryLoggingEnabled, publicBuildPath, getUser, getExtraData } = config;
    if (!sentryLoggingEnabled) {
        return {};
    }

    const environment = getEnvironment();
    const isSupportedBrowser = isBrowserSupported();
    const isLoggingEnabled = isSupportedBrowser || environment.isAutotests;

    if (!isLoggingEnabled) {
        return {};
    }

    const clientConfig = prepareConfig(config);

    const client = new Sentry.BrowserClient({
        ...clientConfig,
        transport: Sentry.makeFetchTransport,
        stackParser: Sentry.defaultStackParser,
        integrations: getIntegrations({
            jsBuildFilterKey: config.jsBuildFilterKey,
            replaysOnErrorSampleRate: clientConfig.replaysOnErrorSampleRate,
            tracesSampleRate: clientConfig.tracesSampleRate,
        }),
        initialScope: getInitialScope({ getUser, getExtraData, environment }),
        debug: config.isDebug,
    });
    const scope = new Sentry.Scope();
    scope.setClient(client);

    const prevOnError = window.onerror;
    const prevOnUnhandledRejection = window.onunhandledrejection;
    window.onerror = (message, url, line, col, error) => {
        if (error && isCurrentServiceError({ error, publicBuildPath, url })) {
            scope.captureException(error);
            return false;
        }
        if (prevOnError) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return prevOnError.bind(window)(message, url, line, col, error);
        }

        return false;
    };
    window.onunhandledrejection = (error) => {
        if (isCurrentServiceError({ error, publicBuildPath })) {
            scope.captureException(error);
            return false;
        }

        if (prevOnUnhandledRejection) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return prevOnUnhandledRejection.bind(window)(error);
        }

        return false;
    };

    return { client, scope };
};

export default initBrowserClient;
